import React, { useState, useEffect } from 'react';
import { Box, Text, Stack, Icon, IconButton, Spinner } from '@chakra-ui/react';
import { FaAngleDown, FaAngleUp } from 'react-icons/fa';
import { legendService, LegendItem } from '@/lib/services/legendService';
import { storageManager } from '@/lib/utils/storage-manager';

const themeDisplayNames: Record<string, string> = {
  soil_zone_sld: 'Soil Zone',
  assoctheme: 'Map Unit',
  dom_ag_cap_sld: 'Dominant Ag. Capability',
  dominant_texture_style: 'Dominant Texture',
  salsym_style_css: 'Salinity Effect on Production',
  phsym_style_css: 'pH',
  initilal_irrig_pooled: 'Initial Irrigation Potential',
};

interface MapLegendProps {
  theme: string;
  isVisible: boolean;
}

export const MapLegend: React.FC<MapLegendProps> = ({ theme, isVisible }) => {
  const [legendData, setLegendData] = useState<LegendItem[]>([]);
  const [themeName, setThemeName] = useState<string>(() => {
    return storageManager.loadTheme();
  });
  const [isCollapsed, setIsCollapsed] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  useEffect(() => {
    const fetchLegendData = async () => {
      if (theme && theme !== 'assoctheme') {
        setIsLoading(true);
        setLegendData([]);
        try {
          const data = await legendService.getColoursForTheme(theme);

          if (data?.resource) {
            setLegendData(data.resource);
            setThemeName(themeDisplayNames[theme] || '');
          }
        } catch (error) {
          console.error('Error fetching legend data:', error);
        } finally {
          setIsLoading(false);
        }
      } else {
        setLegendData([]);
        setIsLoading(false);
      }
    };

    fetchLegendData();
  }, [theme]);

  const getBackgroundColor = (item: LegendItem): string => {
    return `#${item.color}`;
  };

  if (!isVisible || (legendData.length === 0 && !isLoading)) {
    return null;
  }

  return (
    <Box>
      { !isLoading && (
        <>
          <Box
            position='absolute'
            bottom='40px'
            left='16px'
            zIndex={98}
            fontSize='18px'
            backgroundColor='white'
            border='1px solid'
            borderColor='gray.300'
            borderRadius='md'
            p='3'
            minWidth='200px'
            maxWidth='300px'
            boxShadow='lg'
          >
            <Stack direction='row' justify='space-between' align='center' mb={isCollapsed ? 0 : 2}>
              <Text fontSize='sm' fontWeight='bold' color='gray.700'>
                {themeName}
              </Text>
              <Stack direction='row' align='center' gap={2}>
                {isLoading && <Spinner size='xs' color='blue.500' />}
                <IconButton
                  color='gray.600'
                  backgroundColor='white'
                  variant='solid'
                  size='xs'
                  aria-label='Toggle legend'
                  margin='1px'
                  boxShadow='sm'
                  onClick={() => setIsCollapsed(!isCollapsed)}
                >
                  <Icon as={!isCollapsed ? FaAngleDown : FaAngleUp} />
                </IconButton>
              </Stack>
            </Stack>
            {!isCollapsed && !isLoading && (
              <Stack direction='column' align='stretch' gap={1}>
                {legendData.map((item, index) => (
                  <Stack key={index} direction='row' gap={2} align='center'>
                    <Box
                      width='30px'
                      height='15px'
                      backgroundColor={getBackgroundColor(item)}
                      flexShrink={0}
                    />
                    <Text fontSize='xs' color='gray.600' lineHeight='1.2'>
                      {item.title}
                    </Text>
                  </Stack>
                ))}
              </Stack>
            )}
          </Box>
        </>
      )}
    </Box>
  );
};

export default MapLegend;
